import clsx from 'clsx';
import React from 'react';

import type { TableColumnDefinition, TableView } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import {
    createPalette,
    type PaletteCommandDef,
    type PaletteDef,
    PalettePositionWidth,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import { DocumentNodeCommentButton, useDocumentNodeComments } from '../../../../Commenting';
import {
    createInputContentRefPalette,
    useContentReferenceContext,
} from '../../../../ContentReferences';
import { useInsertFiles } from '../../../../InsertFilesDialog';
import { ContentEditorContext } from '../../../contexts';
import { createSelectOptionsPalette } from '../select';
import type { TableDefinitionColorKinds } from '../types';
import { createUsersSelectionPalette } from '../users';

export interface CellActionsConfig {
    /** Key of the parent Table block */
    nodeKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Whether this value in the record is selected */
    selected: boolean;
    /** Is this value editable? */
    readOnly: boolean;
    /** Definition of the column */
    definition: TableColumnDefinition;
    /** Record key */
    recordKey: string;
    /** The color kind assigned to the column */
    definitionColorKind?: TableDefinitionColorKinds;
    /** View to pass to the component */
    view?: TableView;
    /** Current value */
    value: any;
    /** Callback to update the value */
    onValueChange: (value: any) => Promise<void>;
    /** Callback to handle keyboard events */
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
}

export interface UseCellActionsReturn {
    /** React element for the cell actions button */
    cellActionsButton: React.ReactElement | null;
    /** Whether the actions palette is currently open */
    isActionsOpen: boolean;
    /** Function to programmatically open the actions palette */
    openActions: () => void;
}

/**
 * Hook that returns either a comment button or a 'Cell options' palette with both comment _and_ field-specific actions.
 *
 * Example: a content-ref field should allow for add/edit/removing links in the field. Yet a text-field has no special actions, so we just always display a comment button right away.
 */
export function useCellActions(config: CellActionsConfig): UseCellActionsReturn {
    const {
        nodeKey,
        selected,
        readOnly,
        definition,
        value,
        onValueChange,
        onKeyDown,
        editor,
        recordKey,
    } = config;

    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef, {
        onClose: () => {
            buttonRef.current?.focus();
        },
    });

    // Get dependencies for content-ref functionality
    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();

    // Get dependencies for files functionality
    const insertFiles = useInsertFiles();
    const {
        blocks: { withFiles, withImages },
    } = React.useContext(ContentEditorContext);

    // Get comment functionality
    const { onPressNewComment, onPressOpenNodeComment, hasComments, numberOfComments } =
        useDocumentNodeComments({
            nodeKey,
            tableCell: { record: recordKey, definition: definition.id },
        });

    // Focus management
    React.useEffect(() => {
        // Dont focus the button if the cell definition is of type number, rating, checkbox, or text
        if (['number', 'rating', 'checkbox', 'text'].includes(definition.type)) {
            return;
        }

        if (selected && !readOnly) {
            buttonRef.current?.focus();
        }
    }, [selected, readOnly, definition.type]);

    // Build field-specific commands
    const getFieldCommands = (): PaletteCommandDef[] => {
        const cmds: PaletteCommandDef[] = [];
        switch (definition.type) {
            case 'users': {
                const usersValue = Array.isArray(value) ? value : [];
                const label =
                    definition.multiple || usersValue.length === 0 ? 'Add users' : 'Replace user';
                cmds.push({
                    key: 'manage-users',
                    title: label,
                    icon: usersValue.length > 0 ? Icon.Pencil : Icon.Plus,
                    descendant: () =>
                        createUsersSelectionPalette(
                            definition,
                            usersValue,
                            onValueChange,
                            contentReferenceContext.organization
                        ),
                });
                break;
            }
            case 'select': {
                const selectValue = Array.isArray(value) ? value : [];
                const label =
                    selectValue.length > 0
                        ? definition.multiple
                            ? 'Edit options'
                            : 'Edit option'
                        : definition.multiple
                          ? 'Add options'
                          : 'Add option';
                cmds.push({
                    key: 'manage-select',
                    title: label,
                    icon: selectValue.length > 0 ? Icon.Pencil : Icon.Plus,
                    descendant: () =>
                        createSelectOptionsPalette({
                            definition,
                            value: selectValue,
                            nodeKey,
                            editor,
                            onValueChange: async (newValue) => {
                                await onValueChange(newValue);
                            },
                        }),
                });
                break;
            }
            case 'files': {
                if (withFiles) {
                    const filesValue = Array.isArray(value) ? value : [];
                    cmds.push({
                        key: 'add-file',
                        title: filesValue.length > 0 ? 'Add files' : 'Add file',
                        icon: filesValue.length > 0 ? Icon.Pencil : Icon.Plus,
                        trigger: async () => {
                            const refs = await insertFiles.prompt({
                                title: 'Select file',
                            });
                            const newFiles = refs
                                .filter((ref) => ref.kind === 'file')
                                .map((ref) => ref.file);
                            await onValueChange([...filesValue, ...newFiles]);
                        },
                    });
                }
                break;
            }
            case 'image': {
                if (withImages) {
                    cmds.push({
                        key: 'add-image',
                        title: 'Add image',
                        icon: Icon.Plus,
                        trigger: async () => {
                            const [ref] = await insertFiles.prompt({
                                title: 'Select an image',
                                accept: 'images',
                                acceptURL: true,
                            });

                            if (!ref) {
                                return;
                            }

                            await onValueChange(ref);
                        },
                    });
                }
                break;
            }
            case 'content-ref': {
                if (value) {
                    cmds.push(
                        {
                            key: 'edit-link',
                            title: 'Replace link',
                            icon: Icon.Edit,
                            descendant: () =>
                                createInputContentRefPalette(
                                    onValueChange,
                                    contentReferenceContext,
                                    hiveClient,
                                    {
                                        accept: [
                                            'page',
                                            'file',
                                            'space',
                                            'collection',
                                            'anchor',
                                            'url',
                                        ],
                                    }
                                ),
                        },
                        {
                            key: 'remove-link',
                            title: 'Remove link',
                            icon: Icon.Trashcan,
                            trigger: () => {
                                onValueChange(null);
                            },
                        }
                    );
                } else {
                    cmds.push({
                        key: 'add-link',
                        title: 'Add link',
                        icon: Icon.Pencil,
                        descendant: () =>
                            createInputContentRefPalette(
                                onValueChange,
                                contentReferenceContext,
                                hiveClient,
                                {
                                    accept: ['page', 'space', 'anchor', 'file', 'url'],
                                }
                            ),
                    });
                }
                break;
            }
        }
        return cmds;
    };

    const hasFieldCommands = getFieldCommands().length > 0;

    const createActionsPalette = (): PaletteDef => {
        const commentCommands: PaletteCommandDef[] = [];

        if (hasComments) {
            commentCommands.push({
                key: 'view-comments',
                title: 'View comments',
                icon: Icon.CommentThread,
                trigger: onPressOpenNodeComment,
                badge: { label: numberOfComments.toString(), kind: 'info', size: 'small' },
            });
        }

        commentCommands.push({
            key: 'add-comment',
            title: 'Add comment',
            icon: Icon.CommentAdd,
            trigger: onPressNewComment,
        });

        return createPalette({
            position: {
                width: PalettePositionWidth.SMALL,
            },
            groups: [
                {
                    key: 'cell-actions',
                    commands: getFieldCommands(),
                },
                {
                    key: 'comment',
                    commands: commentCommands,
                },
            ],
        });
    };

    const openActions = () => {
        paletteHandle.open(createActionsPalette());
    };

    const buttonPositionClasses = clsx(
        '-translate-y-1/2 absolute top-1/2 right-0 opacity-0 transition-all active:opacity-100 group-hover/tablecell:opacity-100',
        selected || paletteHandle.opened ? 'opacity-100' : 'opacity-0'
    );

    // If there are either field-specific actions OR existing comments, we always show a palette. If not, the button is a simple 'Add comment' button.
    const willShowPalette = hasFieldCommands || hasComments;
    const cellActionsButton = readOnly ? (
        <div className={buttonPositionClasses}>
            <DocumentNodeCommentButton
                hasComments={hasComments}
                numberOfComments={numberOfComments}
                onPress={onPressNewComment}
                onPressNewComment={onPressNewComment}
                kind="secondary"
            />
        </div>
    ) : (
        <Button
            tabIndex={0}
            iconOnly
            kind="secondary"
            size="small"
            ref={buttonRef}
            leadingIcon={willShowPalette ? Icon.MoreVertical : Icon.Comment}
            label={willShowPalette ? 'Cell options' : 'Add comment'}
            active={paletteHandle.opened}
            onPress={willShowPalette ? openActions : onPressNewComment}
            onKeyDown={onKeyDown}
            className={buttonPositionClasses}
            tooltip={{
                label: willShowPalette ? 'Cell options' : 'Add comment',
                shortcut: willShowPalette ? 'space' : undefined,
            }}
        />
    );

    return {
        cellActionsButton,
        isActionsOpen: paletteHandle.opened,
        openActions,
    };
}
